<template>
  <div class="tableWithPerPageSelection" v-if="critereRecherche">
    <div class="entete pdb-20px border-entete">
      <div class="level">
        <div class="level-left">
          <div class="columns is-gapless">
            <div class="column">
              <h1 class="title is-size-3">
                {{ $t('detailPlanningNumerique.title', { typeProduit: findTypeProduit }) }}
              </h1>
              <app-breadcrumb :breadcrumb="breadcrumb" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="column is-narrow is-pulled-left mgt-10px">
      <button class="button" @click='changeDate(-1)'>
        <b-icon icon="chevron-left" size="is-small" type="is-default"></b-icon>
      </button>
    </div>
    <div class="column is-narrow is-pulled-left mgt-10px">
      <b-datepicker show-week-number icon="calendar-today"
        nearby-selectable-month-days
        v-model="dateDebut"
        :editable="true"
        @input="changementPeriode"
      >
      </b-datepicker>
    </div>
    <div class="column is-pulled-left mgt-10px">
      <button class="button" @click='changeDate(1)'>
        <b-icon icon="chevron-right" size="is-small" type="is-default"></b-icon>
      </button>
    </div>
    <div class="column is-narrow is-pulled-left mgt-10px">
      <button class="button" @click='setShowFiltre'>
        <b-icon icon="filter-variant" size="is-small" type="is-default"></b-icon>
        <span class="is-size-7">{{$t('detailPlanningNumerique.actions.filtre')}}
          <label class="has-text-weight-bold"
                  :class="nombreCritere > 0 ? '' : 'is-invisible'">
            ({{ nombreCritere }})
          </label>
        </span>
      </button>
      <span @click="reinitialiser()"
        class="info-circle-right is-pulled-right is-cursor-pointer">
        <b-icon icon="close-circle" type="is-danger"></b-icon>
      </span>
    </div>
    <div class="is-pulled-right mgt-20px">
      <exportExcelButton
        :perm="permsCommande.exporter"
        class="is-inline-block"
        in-progress/>
      <parametrage-table class="mgl-10px"
        :reference-table="referenceTable"
        @endLoadParamtre="preLoadServerTable()">
      </parametrage-table>
    </div>
    <v-server-table class="alignHeaderTab"
                    :columns="columns"
                    :options="options"
                    @loaded="finLoading"
                    @loading="debutLoading"
                    :ref="referenceTable"
                    v-if="startArrayRendering && mountedLoadingEnd">
      <template slot='afterTable'>
        <table-items-in-view
          :perPage="resultPerPage"
          @input="$refs[referenceTable].setLimit($event)"/>
      </template>
      <div slot="societe" slot-scope="props">
        <div class="ellipsis">
          {{ getLibelleSociete(props.row.supportCommercial.societeId) }}
        </div>
      </div>
      <div slot="libelle" slot-scope="props">
        <b-tooltip :label="props.row.libelle" position="is-right">
          <router-link-blank
            :to="{ name: getRouteDetailProduit,
                            params: {id: props.row.id}}"
            class="ellipsis"
            :message="props.row.libelle"></router-link-blank>
        </b-tooltip>
      </div>
      <template slot='enseignePrincipale' slot-scope='props'>
                      <span class='ellipsis'>
                        {{ getEntitePrincipale(props.row) | marqueDenomination }}
                      </span>
      </template>
    </v-server-table>
    <transition name="custom-classes-transition"
                enter-active-class="animate__animated animate__slideInRight"
                leave-active-class="animate__animated animate__slideOutRight" :duration="10">
      <div class="componentBrief" v-show="showFiltre === true">
        <filtre-planning-numerique ref="filtrePlanningNumerique"
                                   @toggleFiltre="setShowFiltre"
                                   v-model="critereRecherche"
                                   @recherche="recherche()"
        />
      </div>
    </transition>
  </div>
</template>

<script>

import { createNamespacedHelpers } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import moment from 'moment';
import {
  TEXTS, NO_PER_PAGE_VALUE, SORT_ICON, RESULTS_PER_PAGE_PLANNING,
} from '@/configuration/clientTableConf';
import planningJour from '@/components/planning/c_planningNumeriqueJour';
import AppBreadcrumb from '@/components/general/breadcrumb';
import { CATEGORIE_REGROUPEMENT_NUMERIQUE } from '@/configuration/catalogue/produitConf';
import { SET_TYPE_PRODUITS, SET_IS_VIEW_UPDATE } from '@/store/modules/produitEcranNumerique-mutation-type';
import filtrePlanningNumerique from '@/components/disponibilite/filtrePlanningNumerique';
import { COMMANDES } from '@/configuration/permissionsBodacConf';
import routerLinkBlank from '@/components/general/routerLinkBlank';
import generatePlanningHeader from '@/services/planningNumerique/headerPlanningNumerique';
import TableItemsInView from '@/components/general/TableItemsInView';
import formatDate from '@/services/general/formatIsoDate';
import messageErreur from '@/services/general/messageErreur';
import { columnsSplice } from '@/services/general/columns';
import { PARAMETRES_INTERNE_KEYS } from '@/store/modules/parametres-mutation-type';
import genererCritereRecherche from '@/services/planningNumerique/genererCritereNumerique';
import parametrageTable from '@/components/general/parametrageTable';
import exportExcelButton from '@/components/general/button/exportExcelButton';
import { REFERENCE_PLANNING_NUMERIQUE, REFERENCE_PLANNING_RESEAU_NUMERIQUE } from '@/configuration/general/referenceTableConf';

const produitNumerique = createNamespacedHelpers('produitEcranNumerique');
const societe = createNamespacedHelpers('societe');
const Parametres = createNamespacedHelpers('parametres');

export default {
  filters: {
    marqueDenomination(marque) {
      return marque ? marque.marque : '-';
    },
  },
  data() {
    return {
      dateDebut: new Date(),
      critereRecherche: {},
      startArrayRendering: false,
      mountedLoadingEnd: false,
      showFiltre: false,
      permsCommande: COMMANDES,
      resultPerPage: RESULTS_PER_PAGE_PLANNING,
    };
  },
  computed: {
    ...Parametres.mapGetters({
      parametresInterne: 'parametresInterne',
    }),
    ...societe.mapGetters({
      societesInformations: 'societesInformations',
      societeActiveId: 'societeActiveId',
      agenceActive: 'agenceActive',
    }),
    ...produitNumerique.mapGetters({
      produits: 'produits',
      totalProduits: 'totalProduits',
      criterePlanning: 'criterePlanning',
    }),
    findTypeProduit() {
      return this.$route.params.typeProduit === CATEGORIE_REGROUPEMENT_NUMERIQUE ? this.$t('detailPlanningNumerique.produit.reseauNum') : this.$t('detailPlanningNumerique.produit.ecranNum');
    },
    referenceTable() {
      return this.$route.params.typeProduit === CATEGORIE_REGROUPEMENT_NUMERIQUE
        ? REFERENCE_PLANNING_RESEAU_NUMERIQUE : REFERENCE_PLANNING_NUMERIQUE;
    },
    breadcrumb() {
      return [
        { title: this.$i18n.t('detailPlanningNumerique.racine'), route: this.$PAGE_DEFAULT, isActive: false },
        { title: this.$i18n.t('detailPlanningNumerique.title', { typeProduit: this.findTypeProduit }), route: { name: '' }, isActive: true },
      ];
    },
    columns() {
      const columns = ['enseignePrincipale', 'libelle', 'entiteCommerciale.audienceAn', 'societe', 'planning'];
      if (this.societesInformations.length === 1 || this.filtrerProduitParSociete
        || this.filtrerProduitParAgence) {
        columnsSplice(columns, 'societe');
      }
      return (this.$route.params.typeProduit === CATEGORIE_REGROUPEMENT_NUMERIQUE) ? columns : columns.filter(item => item !== 'enseignePrincipale');
    },
    nombreCritere() {
      let nombreCritere = this.critereRecherche.libelle ? 1 : 0;
      nombreCritere += this.critereRecherche.enseigneMarque.length;
      nombreCritere += this.critereRecherche.villes.length;
      nombreCritere += this.critereRecherche.enseignePrincipale.length;
      nombreCritere += this.critereRecherche.societeId ? 1 : 0;
      if (this.critereRecherche.nbHabitants.valeur) nombreCritere += 1;
      return nombreCritere <= 9 ? nombreCritere : '+9';
    },
    getRouteDetailProduit() {
      return this.$route.params.typeProduit === CATEGORIE_REGROUPEMENT_NUMERIQUE ? 'c.bodac.catalogue-reseauNumerique' : 'c.bodac.catalogue-ecranNumerique';
    },
    filtrerProduitParAgence() {
      return this.parametresInterne(PARAMETRES_INTERNE_KEYS.filtrerProduitParAgence.key);
    },
    options() {
      return {
        sortIcon: SORT_ICON,
        headings: {
          libelle: this.$i18n.t('detailPlanningNumerique.tab.libelle'),
          'entiteCommerciale.audienceAn': this.$i18n.t('detailPlanningNumerique.tab.audience'),
          enseignePrincipale: this.$i18n.t('detailPlanningNumerique.tab.enseignePrincipale'),
          societe: this.$i18n.t('detailPlanningNumerique.tab.societe'),
          planning: (h) => {
            return generatePlanningHeader(h,
              this.criterePlanning.dateStart, this.criterePlanning.days);
          },
        },
        filterable: false,
        perPageValues: NO_PER_PAGE_VALUE,
        perPage: this.resultPerPage,
        texts: TEXTS,
        templates: {
          planning: planningJour,
        },
        columnsClasses: {
          ...this.columns.reduce((acc, val) => {
            acc[val] = 'sticky';
            return acc;
          }, {}),
          'entiteCommerciale.audienceAn': 'planningTailleColonneAudience sticky',
          planning: 'planningTailleColonnePlanning blockPlanning sticky',
          libelle: 'planningTailleColonneLibelleNumerique sticky',
          enseignePrincipale: 'planningTailleEnseignePrincipale ellipsis sticky',
          societe: 'planningTailleColonneSociete ellipsis sticky',
        },
        sortable: ['produitReseauNumerique.adresse.ville', 'libelle', 'entiteCommerciale.audienceAn'],
        requestFunction: ((data) => {
          if (this.filtrerProduitParAgence && this.critereRecherche.agencesId.length === 0) {
            this.critereRecherche.agencesId = this.agenceActive.map((agence) => agence.agenceId);
          }
          if (!data.query) data.query = this.critereRecherche;
          if (this.filtrerProduitParSociete) {
            data.query.societeId = this.societeActiveId;
          }
          return this.actionsLoadProduits(data);
        }),
        responseAdapter: (() => {
          return {
            data: this.produits,
            count: this.totalProduits,
          };
        }),
      };
    },
    filtrerProduitParSociete() {
      return this.parametresInterne(PARAMETRES_INTERNE_KEYS.filtrerProduitParSociete.key);
    },
  },
  components: {
    AppBreadcrumb,
    filtrePlanningNumerique,
    routerLinkBlank,
    TableItemsInView,
    parametrageTable,
    exportExcelButton,
  },
  methods: {
    ...societe.mapActions({
      loadSocietes: 'loadSocietes',
    }),
    ...produitNumerique.mapMutations({
      setTypeProduit: SET_TYPE_PRODUITS,
      setIsUpdate: SET_IS_VIEW_UPDATE,
    }),
    ...produitNumerique.mapActions({
      actionsLoadProduits: 'loadProduits',
      actionschangeCriterePlanning: 'changeCriterePlanning',
    }),
    ...Parametres.mapActions({
      loadParametresInterne: 'loadParametresInterne',
    }),
    getLibelleSociete(societeId) {
      const societe = this.societesInformations.find((societe) => {
        return societe.id === societeId;
      });
      return societe ? societe.libelle : null;
    },
    getEntitePrincipale(data) {
      return data.entiteCommerciale.enseignesMarques.find(e => e.enseignePrincipale);
    },
    async setShowFiltre() {
      this.showFiltre = !this.showFiltre;
      if (this.showFiltre) await this.$refs.filtrePlanningNumerique.initializeFiltre();
    },
    recherche() {
      if (this.showFiltre) this.setShowFiltre();
      this.$refs[this.referenceTable]?.refresh();
    },
    debutLoading() {
      if (this.loadingComponent) {
        this.loadingComponent.close();
      }
      this.loadingComponent = this.$buefy.loading.open();
    },
    finLoading() {
      this.loadingComponent.close();
    },
    async changementPeriode() {
      if (!this.dateDebut) return;
      const newCritere = cloneDeep(this.criterePlanning);
      newCritere.dateStart = moment(this.dateDebut).startOf('day');
      this.critereRecherche.dateVisualisation = formatDate(this.dateDebut);
      await this.actionschangeCriterePlanning(newCritere);
      if (this.startArrayRendering) this.recherche();
    },
    changeDate(sens) {
      if (!this.dateDebut) return;
      this.dateDebut = (moment(this.dateDebut).add(this.criterePlanning.days * sens, 'days')).toDate();
      this.changementPeriode();
    },
    async reinitialiser() {
      await this.$refs.filtrePlanningNumerique.initializeFiltre();
      this.$refs.filtrePlanningNumerique.viderCritere();
      this.$refs.filtrePlanningNumerique.appliquerCritere();
      this.showFiltre = false;
    },
    async preLoadServerTable() {
      this.startArrayRendering = true;
      await this.changementPeriode();
    },
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.changementPeriode);
  },
  async mounted() {
    const loadingComponent = this.$buefy.loading.open();
    try {
      await this.loadSocietes({});
      await this.loadParametresInterne([PARAMETRES_INTERNE_KEYS.filtrerProduitParSociete.key,
        PARAMETRES_INTERNE_KEYS.filtrerProduitParAgence.key]);
      if (this.filtrerProduitParAgence) {
        this.critereRecherche.agencesId = this.agenceActive.map((agence) => agence.agenceId);
      }
      if (this.$route.params.typeProduit === CATEGORIE_REGROUPEMENT_NUMERIQUE) {
        this.setInformationPage(this.$t('pageGescom.planning.reseauNumerique'));
      } else this.setInformationPage(this.$t('pageGescom.planning.ecranNumerique'));
      this.mountedLoadingEnd = true;
      loadingComponent.close();
    } catch (e) {
      messageErreur(this.$i18n);
      loadingComponent.close();
      throw e;
    }
  },
  created() {
    window.addEventListener('resize', this.changementPeriode);
    this.setTypeProduit(this.$route.params.typeProduit);
    this.setIsUpdate(false);
    this.critereRecherche = cloneDeep(genererCritereRecherche());
  },
};

</script>
